/**
 * Messages for one area of the app (document 15, section 5.1), English and Arabic side by side.
 * Every key lives under `salesPages`; Arabic plurals have all six CLDR forms.
 */
import * as more from './salesPages/more.js';
import * as documentForm from './salesPages/documentForm.js';
import * as drawers from './salesPages/drawers.js';
import * as extra from './salesPages/extra.js';
import * as records from './salesPages/records.js';
import * as screens from './salesPages/screens.js';

export const en = {
  salesPages: {
    ...more.en,
    ...screens.en,
    ...records.en,
    ...documentForm.en,
    ...drawers.en,
    ...extra.en,
    // Shared vocabulary (lib/sales.ts, lib/contacts.ts, lib/items.ts).
    kind: {
      invoice: 'Invoice',
      credit_note: 'Credit note',
      sales_receipt: 'Sales receipt',
      quote: 'Quote',
      sales_order: 'Sales order',
      delivery_challan: 'Delivery challan',
      payment: 'Payment',
    },
    kindNumber: {
      invoice: 'Invoice no.',
      credit_note: 'Credit note no.',
      sales_receipt: 'Sales receipt no.',
      quote: 'Quote no.',
      sales_order: 'SO no.',
      delivery_challan: 'Challan no.',
    },
    kindDate: {
      invoice: 'Invoice date',
      credit_note: 'Credit note date',
      sales_receipt: 'Sales receipt date',
      quote: 'Quote date',
      sales_order: 'Sales order date',
      delivery_challan: 'Challan date',
    },
    status: {
      cancelled: 'Cancelled',
      void: 'Void',
      draft: 'Draft',
      open: 'Open',
      closed: 'Closed',
      pending: 'Pending',
      accepted: 'Accepted',
      declined: 'Declined',
      converted: 'Converted',
      applied: 'Applied',
      partlyApplied: 'Partly applied',
      unapplied: 'Unapplied',
      paid: 'Paid',
      overduePartlyPaid: 'Overdue · partly paid',
      overdue: 'Overdue',
      partiallyPaid: 'Partially paid',
      unpaid: 'Unpaid',
    },
    paymentMethod: {
      bank_transfer: 'Bank transfer',
      cheque: 'Cheque',
      cash: 'Cash',
      card: 'Credit card',
    },
    challanType: {
      job_work: 'Job work',
      supply_on_approval: 'Supply on approval',
      liquid_gas: 'Supply of liquid gas',
      others: 'Other than by way of supply',
    },
    emailStatus: {
      recorded: 'Recorded',
      sent: 'Sent',
      failed: 'Failed',
    },
    itemKind: {
      service: 'Service',
      non_inventory: 'Non-inventory',
      inventory: 'Inventory',
    },
    contactKind: {
      customer: { one: 'Customer', many: 'Customers' },
      vendor: { one: 'Vendor', many: 'Vendors' },
    },
    country: {
      AE: 'United Arab Emirates',
      SA: 'Saudi Arabia',
      OM: 'Oman',
      QA: 'Qatar',
      BH: 'Bahrain',
      KW: 'Kuwait',
      EG: 'Egypt',
      JO: 'Jordan',
      LB: 'Lebanon',
      TR: 'Türkiye',
      IN: 'India',
      PK: 'Pakistan',
      LK: 'Sri Lanka',
      BD: 'Bangladesh',
      PH: 'Philippines',
      CN: 'China',
      HK: 'Hong Kong',
      SG: 'Singapore',
      JP: 'Japan',
      GB: 'United Kingdom',
      IE: 'Ireland',
      DE: 'Germany',
      FR: 'France',
      IT: 'Italy',
      NL: 'Netherlands',
      SE: 'Sweden',
      CH: 'Switzerland',
      US: 'United States',
      CA: 'Canada',
      AU: 'Australia',
      ZA: 'South Africa',
    },
    poBox: 'P.O. Box {{box}}',
    // Sales form and document pages (document-form, document-page, dialogs).
    document: {
      activity: 'Activity',
      voidDate: 'Void date',
      voidDateHint: 'The reversing entry is dated here.',
      enterVoidDate: 'Enter the void date.',
      voidBeforeDocument: 'The void date cannot be before the document date.',
      reason: 'Reason',
      reasonHint: 'Recorded in the audit log.',
      voiding: 'Voiding…',
      void: 'Void',
      notFound: 'Document not found',
      otherKind: 'This link points at a different kind of document.',
      chooseCustomer: 'Choose a customer.',
      enterDate: 'Enter the date.',
      dueBeforeDate: 'The {{due}} cannot be before the date.',
      dueDateLower: 'due date',
      addLine: 'Add at least one line.',
      lineQuantity: 'Line {{n}}: enter a quantity above zero, with at most 6 decimals.',
      lineTaxCode: 'Line {{n}}: choose a {{tax}} code.',
      overApplyInvoice: '{{number}}: you can apply at most {{amount}}, its open balance.',
      overApplyCredit: '{{number}}: only {{amount}} of credit is left.',
      applyBeforeCredit: 'The date applied cannot be before the credit note date.',
      creditApplied: 'Credit note {{number}}: {{amount}} applied.',
      applyCreditTitle: 'Apply credit note {{number}}',
      applyCreditBody:
        '{{amount}} available for {{name}}. Applying posts nothing new: the credit note already reduced accounts receivable.',
      dateApplied: 'Date applied',
      openInvoices: 'Open invoices',
      colInvoice: 'Invoice',
      colDueDate: 'Due date',
      colOpenBalance: 'Open balance',
      colApply: 'Apply',
      applyTo: 'Apply to {{number}}',
      noOpenInvoices: 'This customer has no open invoices.',
      applyingOf: 'Applying {{amount}} of {{available}}.',
      applying: 'Applying…',
      apply: 'Apply',
    },
    // Customers and vendors (contact-*, contact form validation).
    contact: {},
    // Products and services, inventory adjustment, projects.
    item: {},
    inventory: {},
    project: {},
    // Lists, payments, statements (sales-table, order-list, receive-payment, routes).
    list: {
      unpaid: 'Unpaid',
      paidLast30: 'Paid (last 30 days)',
      overdueCount_one: '{{count}} overdue',
      overdueCount_other: '{{count}} overdue',
      notDueCount_one: '{{count}} not due yet',
      notDueCount_other: '{{count}} not due yet',
      notDepositedCount_one: '{{count}} not deposited',
      notDepositedCount_other: '{{count}} not deposited',
      depositedCount_one: '{{count}} deposited',
      depositedCount_other: '{{count}} deposited',
      all: 'All',
      statusFilter: 'Status',
      salesOrders: 'Sales orders',
      deliveryChallans: 'Delivery challans',
      newSalesOrder: 'New sales order',
      newDeliveryChallan: 'New delivery challan',
      searchSalesOrders: 'Search by customer or SO no.',
      searchDeliveryChallans: 'Search by customer or Challan no.',
      searchSalesOrdersLabel: 'Search sales orders',
      searchDeliveryChallansLabel: 'Search delivery challans',
      noSalesOrders: 'No sales orders match these filters',
      noDeliveryChallans: 'No delivery challans match these filters',
      selectedCount_one: '{{count}} selected',
      selectedCount_other: '{{count}} selected',
      batchHint: 'Select transactions to send, print or void them together.',
      selectAll: 'Select all',
      selectRow: 'Select {{type}} {{number}}',
      draftLower: 'draft',
      colDate: 'Date',
      colType: 'Type',
      colNo: 'No.',
      colCustomer: 'Customer',
      colMemo: 'Memo',
      colDueDate: 'Due date',
      colBalance: 'Balance',
      colTotal: 'Total',
      colStatus: 'Status',
      colEInvoice: 'E-invoice',
      colAction: 'Action',
      transactionCount_one: '{{count}} transaction',
      transactionCount_other: '{{count}} transactions',
      approvedPosted: '{{kind}} {{number}} approved and posted.',
      view: 'View',
      approve: 'Approve',
      receivePayment: 'Receive payment',
      convertToInvoice: 'Convert to invoice',
      createInvoice: 'Create invoice',
      apply: 'Apply',
      skipPayment: 'payments are voided from their own screen',
      skipQuote: 'quotes are not posted',
      skipDraft: 'drafts are not posted',
      skipVoid: 'already void',
      voidedCount_one: '{{count}} transaction voided.',
      voidedCount_other: '{{count}} transactions voided.',
      notVoided: 'Not voided: {{list}}.',
      skipped: 'Skipped: {{list}}.',
      listItem: '{{number}} ({{reason}})',
      listSeparator: '; ',
      batchActions: 'Batch actions',
      send: 'Send',
      print: 'Print',
      generateIrns: 'Generate IRNs',
      void: 'Void',
      voidCountTitle_one: 'Void {{count}} transaction',
      voidCountTitle_other: 'Void {{count}} transactions',
      voidBatchBody:
        'Each one gets a reversing entry, releases any payments or credits applied to it and keeps its number. Drafts, quotes and payments in the selection are left as they are. This cannot be undone.',
      emailsSent_one: '{{count}} email sent',
      emailsSent_other: '{{count}} emails sent',
      emailsRecorded_one: '{{count}} email recorded',
      emailsRecorded_other: '{{count}} emails recorded',
      emailsFailed: '{{count}} failed',
      notSent: 'not sent: {{list}}',
      sentEmails: 'Sent emails',
      irnsGenerated_one: '{{count}} IRN generated',
      irnsGenerated_other: '{{count}} IRNs generated',
      irnsSkipped: 'skipped: {{list}}',
      sentence: '{{text}}.',
      sentenceJoin: '. ',
    },
    payment: {},
    statement: {},
  },
};

export const ar = {
  salesPages: {
    ...more.ar,
    ...screens.ar,
    ...records.ar,
    ...documentForm.ar,
    ...drawers.ar,
    ...extra.ar,
    kind: {
      invoice: 'فاتورة',
      credit_note: 'إشعار دائن',
      sales_receipt: 'إيصال مبيعات',
      quote: 'عرض سعر',
      sales_order: 'أمر بيع',
      delivery_challan: 'إذن تسليم',
      payment: 'سند قبض',
    },
    kindNumber: {
      invoice: 'رقم الفاتورة',
      credit_note: 'رقم الإشعار الدائن',
      sales_receipt: 'رقم إيصال المبيعات',
      quote: 'رقم عرض السعر',
      sales_order: 'رقم أمر البيع',
      delivery_challan: 'رقم إذن التسليم',
    },
    kindDate: {
      invoice: 'تاريخ الفاتورة',
      credit_note: 'تاريخ الإشعار الدائن',
      sales_receipt: 'تاريخ إيصال المبيعات',
      quote: 'تاريخ عرض السعر',
      sales_order: 'تاريخ أمر البيع',
      delivery_challan: 'تاريخ إذن التسليم',
    },
    status: {
      cancelled: 'ملغى',
      void: 'ملغاة',
      draft: 'مسودة',
      open: 'مفتوح',
      closed: 'مغلق',
      pending: 'قيد الانتظار',
      accepted: 'مقبول',
      declined: 'مرفوض',
      converted: 'محوّل',
      applied: 'مطبّق',
      partlyApplied: 'مطبّق جزئياً',
      unapplied: 'غير مطبّق',
      paid: 'مدفوعة',
      overduePartlyPaid: 'متأخرة · مدفوعة جزئياً',
      overdue: 'متأخرة',
      partiallyPaid: 'مدفوعة جزئياً',
      unpaid: 'غير مدفوعة',
    },
    paymentMethod: {
      bank_transfer: 'تحويل بنكي',
      cheque: 'شيك',
      cash: 'نقداً',
      card: 'بطاقة ائتمان',
    },
    challanType: {
      job_work: 'أعمال تصنيع لدى الغير',
      supply_on_approval: 'توريد بشرط الموافقة',
      liquid_gas: 'توريد غاز مسال',
      others: 'غير ذلك من غير طريق التوريد',
    },
    emailStatus: {
      recorded: 'مسجّل',
      sent: 'مُرسل',
      failed: 'فشل',
    },
    itemKind: {
      service: 'خدمة',
      non_inventory: 'غير مخزني',
      inventory: 'مخزني',
    },
    contactKind: {
      customer: { one: 'عميل', many: 'العملاء' },
      vendor: { one: 'مورد', many: 'الموردون' },
    },
    country: {
      AE: 'الإمارات العربية المتحدة',
      SA: 'المملكة العربية السعودية',
      OM: 'عُمان',
      QA: 'قطر',
      BH: 'البحرين',
      KW: 'الكويت',
      EG: 'مصر',
      JO: 'الأردن',
      LB: 'لبنان',
      TR: 'تركيا',
      IN: 'الهند',
      PK: 'باكستان',
      LK: 'سريلانكا',
      BD: 'بنغلاديش',
      PH: 'الفلبين',
      CN: 'الصين',
      HK: 'هونغ كونغ',
      SG: 'سنغافورة',
      JP: 'اليابان',
      GB: 'المملكة المتحدة',
      IE: 'أيرلندا',
      DE: 'ألمانيا',
      FR: 'فرنسا',
      IT: 'إيطاليا',
      NL: 'هولندا',
      SE: 'السويد',
      CH: 'سويسرا',
      US: 'الولايات المتحدة',
      CA: 'كندا',
      AU: 'أستراليا',
      ZA: 'جنوب أفريقيا',
    },
    poBox: 'ص.ب. {{box}}',
    document: {
      activity: 'السجل',
      voidDate: 'تاريخ الإلغاء',
      voidDateHint: 'يُؤرَّخ القيد العكسي بهذا التاريخ.',
      enterVoidDate: 'أدخل تاريخ الإلغاء.',
      voidBeforeDocument: 'لا يمكن أن يسبق تاريخ الإلغاء تاريخ المستند.',
      reason: 'السبب',
      reasonHint: 'يُسجَّل في سجل التدقيق.',
      voiding: 'جارٍ الإلغاء…',
      void: 'إلغاء',
      notFound: 'المستند غير موجود',
      otherKind: 'يشير هذا الرابط إلى نوع آخر من المستندات.',
      chooseCustomer: 'اختر عميلاً.',
      enterDate: 'أدخل التاريخ.',
      dueBeforeDate: 'لا يمكن أن يسبق {{due}} التاريخ.',
      dueDateLower: 'تاريخ الاستحقاق',
      addLine: 'أضف بنداً واحداً على الأقل.',
      lineQuantity: 'البند {{n}}: أدخل كمية أكبر من صفر، بستة منازل عشرية كحد أقصى.',
      lineTaxCode: 'البند {{n}}: اختر رمز {{tax}}.',
      overApplyInvoice: '{{number}}: يمكنك تطبيق {{amount}} كحد أقصى، وهو رصيدها المفتوح.',
      overApplyCredit: '{{number}}: لم يتبقَّ من الرصيد الدائن سوى {{amount}}.',
      applyBeforeCredit: 'لا يمكن أن يسبق تاريخ التطبيق تاريخ الإشعار الدائن.',
      creditApplied: 'الإشعار الدائن {{number}}: تم تطبيق {{amount}}.',
      applyCreditTitle: 'تطبيق الإشعار الدائن {{number}}',
      applyCreditBody:
        '{{amount}} متاح لـ {{name}}. لا يُرحِّل التطبيق أي قيد جديد: فقد خفّض الإشعار الدائن الذمم المدينة مسبقاً.',
      dateApplied: 'تاريخ التطبيق',
      openInvoices: 'الفواتير المفتوحة',
      colInvoice: 'الفاتورة',
      colDueDate: 'تاريخ الاستحقاق',
      colOpenBalance: 'الرصيد المفتوح',
      colApply: 'تطبيق',
      applyTo: 'تطبيق على {{number}}',
      noOpenInvoices: 'لا توجد فواتير مفتوحة لهذا العميل.',
      applyingOf: 'تطبيق {{amount}} من {{available}}.',
      applying: 'جارٍ التطبيق…',
      apply: 'تطبيق',
    },
    contact: {},
    item: {},
    inventory: {},
    project: {},
    list: {
      unpaid: 'غير مدفوعة',
      paidLast30: 'مدفوعة (آخر 30 يوماً)',
      overdueCount_zero: 'لا توجد فواتير متأخرة',
      overdueCount_one: 'فاتورة واحدة متأخرة',
      overdueCount_two: 'فاتورتان متأخرتان',
      overdueCount_few: '{{count}} فواتير متأخرة',
      overdueCount_many: '{{count}} فاتورةً متأخرة',
      overdueCount_other: '{{count}} فاتورة متأخرة',
      notDueCount_zero: 'لا توجد فواتير غير مستحقة بعد',
      notDueCount_one: 'فاتورة واحدة غير مستحقة بعد',
      notDueCount_two: 'فاتورتان غير مستحقتين بعد',
      notDueCount_few: '{{count}} فواتير غير مستحقة بعد',
      notDueCount_many: '{{count}} فاتورةً غير مستحقة بعد',
      notDueCount_other: '{{count}} فاتورة غير مستحقة بعد',
      notDepositedCount_zero: 'لا توجد دفعات غير مودعة',
      notDepositedCount_one: 'دفعة واحدة غير مودعة',
      notDepositedCount_two: 'دفعتان غير مودعتين',
      notDepositedCount_few: '{{count}} دفعات غير مودعة',
      notDepositedCount_many: '{{count}} دفعةً غير مودعة',
      notDepositedCount_other: '{{count}} دفعة غير مودعة',
      depositedCount_zero: 'لا توجد دفعات مودعة',
      depositedCount_one: 'دفعة واحدة مودعة',
      depositedCount_two: 'دفعتان مودعتان',
      depositedCount_few: '{{count}} دفعات مودعة',
      depositedCount_many: '{{count}} دفعةً مودعة',
      depositedCount_other: '{{count}} دفعة مودعة',
      all: 'الكل',
      statusFilter: 'الحالة',
      salesOrders: 'أوامر البيع',
      deliveryChallans: 'أذونات التسليم',
      newSalesOrder: 'أمر بيع جديد',
      newDeliveryChallan: 'إذن تسليم جديد',
      searchSalesOrders: 'البحث باسم العميل أو رقم أمر البيع',
      searchDeliveryChallans: 'البحث باسم العميل أو رقم إذن التسليم',
      searchSalesOrdersLabel: 'البحث في أوامر البيع',
      searchDeliveryChallansLabel: 'البحث في أذونات التسليم',
      noSalesOrders: 'لا توجد أوامر بيع تطابق هذه المرشحات',
      noDeliveryChallans: 'لا توجد أذونات تسليم تطابق هذه المرشحات',
      selectedCount_zero: 'لم يُحدَّد شيء',
      selectedCount_one: 'معاملة واحدة محددة',
      selectedCount_two: 'معاملتان محددتان',
      selectedCount_few: '{{count}} معاملات محددة',
      selectedCount_many: '{{count}} معاملةً محددة',
      selectedCount_other: '{{count}} معاملة محددة',
      batchHint: 'حدّد المعاملات لإرسالها أو طباعتها أو إلغائها معاً.',
      selectAll: 'تحديد الكل',
      selectRow: 'تحديد {{type}} {{number}}',
      draftLower: 'مسودة',
      colDate: 'التاريخ',
      colType: 'النوع',
      colNo: 'الرقم',
      colCustomer: 'العميل',
      colMemo: 'المذكرة',
      colDueDate: 'تاريخ الاستحقاق',
      colBalance: 'الرصيد',
      colTotal: 'الإجمالي',
      colStatus: 'الحالة',
      colEInvoice: 'الفاتورة الإلكترونية',
      colAction: 'الإجراء',
      transactionCount_zero: 'لا توجد معاملات',
      transactionCount_one: 'معاملة واحدة',
      transactionCount_two: 'معاملتان',
      transactionCount_few: '{{count}} معاملات',
      transactionCount_many: '{{count}} معاملةً',
      transactionCount_other: '{{count}} معاملة',
      approvedPosted: '{{kind}} {{number}}: تم الاعتماد والترحيل.',
      view: 'عرض',
      approve: 'اعتماد',
      receivePayment: 'استلام دفعة',
      convertToInvoice: 'تحويل إلى فاتورة',
      createInvoice: 'إنشاء فاتورة',
      apply: 'تطبيق',
      skipPayment: 'تُلغى سندات القبض من شاشتها الخاصة',
      skipQuote: 'عروض الأسعار غير مرحّلة',
      skipDraft: 'المسودات غير مرحّلة',
      skipVoid: 'ملغاة مسبقاً',
      voidedCount_zero: 'لم تُلغَ أي معاملة.',
      voidedCount_one: 'أُلغيت معاملة واحدة.',
      voidedCount_two: 'أُلغيت معاملتان.',
      voidedCount_few: 'أُلغيت {{count}} معاملات.',
      voidedCount_many: 'أُلغيت {{count}} معاملةً.',
      voidedCount_other: 'أُلغيت {{count}} معاملة.',
      notVoided: 'لم تُلغَ: {{list}}.',
      skipped: 'تم تخطي: {{list}}.',
      listItem: '{{number}} ({{reason}})',
      listSeparator: '؛ ',
      batchActions: 'إجراءات جماعية',
      send: 'إرسال',
      print: 'طباعة',
      generateIrns: 'إنشاء أرقام IRN',
      void: 'إلغاء',
      voidCountTitle_zero: 'إلغاء المعاملات',
      voidCountTitle_one: 'إلغاء معاملة واحدة',
      voidCountTitle_two: 'إلغاء معاملتين',
      voidCountTitle_few: 'إلغاء {{count}} معاملات',
      voidCountTitle_many: 'إلغاء {{count}} معاملةً',
      voidCountTitle_other: 'إلغاء {{count}} معاملة',
      voidBatchBody:
        'يُسجَّل لكل معاملة قيد عكسي، وتُحرَّر أي دفعات أو أرصدة دائنة مطبّقة عليها، ويبقى رقمها كما هو. تبقى المسودات وعروض الأسعار وسندات القبض المحددة كما هي. لا يمكن التراجع عن ذلك.',
      emailsSent_zero: 'لم تُرسل أي رسالة',
      emailsSent_one: 'أُرسلت رسالة واحدة',
      emailsSent_two: 'أُرسلت رسالتان',
      emailsSent_few: 'أُرسلت {{count}} رسائل',
      emailsSent_many: 'أُرسلت {{count}} رسالةً',
      emailsSent_other: 'أُرسلت {{count}} رسالة',
      emailsRecorded_zero: 'لم تُسجَّل أي رسالة',
      emailsRecorded_one: 'سُجّلت رسالة واحدة',
      emailsRecorded_two: 'سُجّلت رسالتان',
      emailsRecorded_few: 'سُجّلت {{count}} رسائل',
      emailsRecorded_many: 'سُجّلت {{count}} رسالةً',
      emailsRecorded_other: 'سُجّلت {{count}} رسالة',
      emailsFailed: 'فشل {{count}}',
      notSent: 'لم تُرسل: {{list}}',
      sentEmails: 'الرسائل المرسلة',
      irnsGenerated_zero: 'لم يُنشأ أي رقم IRN',
      irnsGenerated_one: 'أُنشئ رقم IRN واحد',
      irnsGenerated_two: 'أُنشئ رقما IRN',
      irnsGenerated_few: 'أُنشئت {{count}} أرقام IRN',
      irnsGenerated_many: 'أُنشئ {{count}} رقماً IRN',
      irnsGenerated_other: 'أُنشئ {{count}} رقم IRN',
      irnsSkipped: 'تم تخطي: {{list}}',
      sentence: '{{text}}.',
      sentenceJoin: '. ',
    },
    payment: {},
    statement: {},
  },
};
